
import { nameCase } from "@foundernest/namecase";
import Avatar from "primevue/avatar";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import { TranslateResult } from "vue-i18n";
import { Component, Emit, Mixins } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import { Assistant, Representative, Role } from "@/global";
import StateMixin from "@/mixins/stateMixin";
import AccountModule from "@/store/modules/account";
import AuditEventModule from "@/store/modules/auditEvents";
import ConsentModule from "@/store/modules/consent";
import DocumentModule from "@/store/modules/document";
import VaccinationModule from "@/store/modules/vaccination";
import { getShortName } from "@/utils";

@Component({
  components: {
    Button,
    Avatar,
    Dropdown,
  },
})
export default class ProxySelect extends Mixins(StateMixin) {
  private readonly accountState = getModule(AccountModule);
  private readonly documentState = getModule(DocumentModule);
  private readonly vaccinationState = getModule(VaccinationModule);
  private readonly consentState = getModule(ConsentModule);
  private readonly logState = getModule(AuditEventModule);

  isAssistant(person: any): person is Assistant {
    if (person.gln) return true;
    return false;
  }

  // person: Representative | Assistant
  async selectProxy(person: any) {
    try {
      await this.accountState.selectProxy(person);
      if ([Role.Representative, Role.Assistant].includes(this.accountState.role)) {
        this.clearUserData();
        this.refetchCurrentResource();
      }
    } catch (error) {
      this.$notify({
        text: this.$t("toasts.account.xuaFetchFail").toString(),
        type: "error",
        duration: -1,
      });
    }
  }

  async deselectProxy() {
    this.clearUserData();

    switch (this.accountState.role) {
      case Role.Patient:
      case Role.Representative:
        this.accountState.setRole(Role.Patient);
        this.accountState.clearProxyPerson();
        this.accountState.clearXUAToken();
        this.accountState.clearPatientSPID();

        this.refetchCurrentResource();
        break;
      case Role.HealthcareProfessional:
      case Role.Assistant:
        this.accountState.setRole(Role.HealthcareProfessional);
        this.accountState.clearProxyPerson();
        this.accountState.logOutPatient();
        if (this.$route.name != "patients") {
          this.$router.push({ name: "patients" });
        }
        break;
    }

    this.accountState.fetchProxies();
  }

  get disabled(): boolean {
    return (
      this.documentState.isLoading ||
      this.vaccinationState.fetching ||
      this.consentState.isLoading ||
      this.logState.isLoading
    );
  }

  get showProxySelection(): boolean {
    switch (this.accountState.role) {
      case Role.HealthcareProfessional:
        return this.accountState.selectedPatient ? false : true;
      case Role.Patient:
        return true;
    }
    return false;
  }

  @Emit("change")
  handleChange(event: { originalEvent: Event; value: Representative | Assistant }) {
    if (event.value) {
      this.selectProxy(event.value);
    } else {
      this.deselectProxy();
    }
  }

  handleShow() {
    if (this.accountState.proxyPerson) {
      this.deselectProxy();
    }
  }

  handleHide() {
    (document.activeElement as HTMLElement | null)?.blur();
  }

  get startText(): TranslateResult {
    switch (this.accountState.role) {
      case Role.Patient:
      case Role.Representative:
        return this.$t("navigation.proxies.startRepresentation");
      case Role.HealthcareProfessional:
      case Role.Assistant:
        return this.$t("navigation.proxies.startAssistance");
      default:
        return "";
    }
  }

  get stopText(): TranslateResult {
    switch (this.accountState.role) {
      case Role.HealthcareProfessional:
      case Role.Assistant:
        return this.$t("navigation.proxies.stopAssistance");
      case Role.Patient:
      case Role.Representative:
        return this.$t("navigation.proxies.stopRepresentation");
      default:
        return "";
    }
  }

  get emptyText(): TranslateResult {
    switch (this.accountState.role) {
      case Role.Patient:
      case Role.Representative:
        return this.$t("navigation.proxies.noRepresentatives");
      case Role.HealthcareProfessional:
      case Role.Assistant:
        return this.$t("navigation.proxies.noAssistants");
      default:
        return this.$t("navigation.proxies.noRepresentatives");
    }
  }

  get activeLabel(): TranslateResult {
    switch (this.accountState.role) {
      case Role.HealthcareProfessional:
      case Role.Assistant:
        return this.$t("navigation.proxies.assisting");
      case Role.Patient:
      case Role.Representative:
        return this.$t("navigation.proxies.representing");
      default:
        return "";
    }
  }

  get fullName(): string | null {
    const person = this.accountState.proxyPerson as any;
    if (!person) return null;

    if (Object.hasOwn(person, "firstName") && Object.hasOwn(person, "lastName")) {
      return `${person.firstName} ${person.lastName}`;
    }

    if (Object.hasOwn(person, "name")) {
      return person.name;
    }

    return null;
  }

  get shortName(): string | null {
    return this.fullName ? getShortName(this.fullName, 30) : null;
  }

  get name(): string | null {
    if (!this.accountState.proxyPerson) return null;

    return this.shortName ? nameCase(this.shortName, { lazy: false }) : null;
  }

  getName(person: any): string {
    if (!person) {
      return "";
    }
    const fullName = person.name || `${person.firstName} ${person.lastName}`;
    const shortName = getShortName(fullName, 30);
    return nameCase(shortName, { lazy: false });
  }
}
